import {
    Box,
    Divider,
    Flex,
    Form,
    MatrixPicker,
    Radio,
    Select,
    Text,
    WeekDayPicker,
} from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { memo, ReactNode } from 'react';

import { schedulerFormNames } from '../../Scheduler.constants';
import { daysItems, daysOfWeekItems, weekNumbersItems } from './SchedulerDay.constants';
import { useSchedulerRadioChecked } from './SchedulerDay.hooks';
import { messages } from './SchedulerDay.messages';

export const SchedulerDay = memo(() => {
    const { schedulerRadioChecked, handleRadioCheckedChange } = useSchedulerRadioChecked();

    return (
        <Form.Part>
            <Radio.Group
                direction='column'
                spacing='16'
                value={schedulerRadioChecked}
                onChange={handleRadioCheckedChange}
                name={schedulerFormNames.dayOption}
            >
                <Radio value={domain.ScheduleDayType.EveryDay} size='small'>
                    {messages.everyDay}
                </Radio>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.EveryDaysOfMonth} size='small'>
                        {messages.every}

                        {schedulerRadioChecked !== domain.ScheduleDayType.EveryDaysOfMonth && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.everyDaysOfMonth({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.EveryDaysOfMonth && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select name={schedulerFormNames.nthDayOfMonth} size='small' items={daysItems} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.startingDays}
                            </Text>

                            <Box width={64}>
                                <Select name={schedulerFormNames.startingDayOfMonth} size='small' items={daysItems} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.month}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.SpecificDayOfWeek} size='small'>
                        {messages.specificDayOfWeek}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.SpecificDayOfWeek && (
                        <WeekDayPicker name={schedulerFormNames.weekday} spacing='4' />
                    )}
                </Flex>

                <Divider />

                <Flex spacing='8' direction='column'>
                    <Radio value={domain.ScheduleDayType.SpecificDayOfMonth} size='small'>
                        {messages.specificDayOfMonth}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.SpecificDayOfMonth && (
                        <Box width={238 + 24} spacing='0 0 0 24'>
                            <MatrixPicker
                                name={schedulerFormNames.specificDayOfMonth}
                                maxColumns={7}
                                items={daysItems}
                            />
                        </Box>
                    )}
                </Flex>

                <Divider />

                <Radio value={domain.ScheduleDayType.LastDayOfMonth} size='small'>
                    {messages.lastDayOfMonth}
                </Radio>

                <Divider />

                <Radio value={domain.ScheduleDayType.LastWeekdayOfMonth} size='small'>
                    {messages.lastWeekdayOfMonth}
                </Radio>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.LastDayOfWeekOfMonth} size='small'>
                        {messages.last}

                        {schedulerRadioChecked !== domain.ScheduleDayType.LastDayOfWeekOfMonth && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.lastDayOfWeekOfMonth({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.LastDayOfWeekOfMonth && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={144}>
                                <Select
                                    name={schedulerFormNames.lastDayOfWeekOfMonth}
                                    size='small'
                                    items={daysOfWeekItems}
                                />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.month}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.LastNDaysBeforeMonthEnd} size='small'>
                        {messages.last}

                        {schedulerRadioChecked !== domain.ScheduleDayType.LastNDaysBeforeMonthEnd && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.lastNDaysBeforeMonthEnd({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.LastNDaysBeforeMonthEnd && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select
                                    name={schedulerFormNames.lastNDaysBeforeMonthEnd}
                                    size='small'
                                    items={daysItems}
                                />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.beforeEnd}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.NearestWeekdayToDay} size='small'>
                        {messages.nearestWeekday}

                        {schedulerRadioChecked !== domain.ScheduleDayType.NearestWeekdayToDay && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.nearestWeekdayToDay({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.NearestWeekdayToDay && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select name={schedulerFormNames.nearestWeekdayToDay} size='small' items={daysItems} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.month}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleDayType.DayAndWeekdayOfMonth} size='small'>
                        {messages.onThe}

                        {schedulerRadioChecked !== domain.ScheduleDayType.DayAndWeekdayOfMonth && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.dayAndWeekdayOfMonth({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleDayType.DayAndWeekdayOfMonth && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select
                                    name={schedulerFormNames.nthDayOfWeekMonth}
                                    size='small'
                                    items={weekNumbersItems}
                                />
                            </Box>

                            <Box width={144}>
                                <Select
                                    name={schedulerFormNames.dayOfWeekOfMonth}
                                    size='small'
                                    items={daysOfWeekItems}
                                />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.month}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            </Radio.Group>
        </Form.Part>
    );
});

SchedulerDay.displayName = 'SchedulerDay';
